import { useState } from "react";
import { Download, Eye, FileText, Image as ImageIcon, Presentation } from "lucide-react";
import { toast } from "sonner";
import { downloadMaterial, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ViewerDialog } from "@/components/ViewerDialog";
import { kindName, subjectById, formatSize, formatDate } from "@/lib/constants";

const IMG = ["jpg", "jpeg", "png", "gif", "webp", "heic", "bmp"];
const fileIcon = (ext) => (IMG.includes(ext) ? ImageIcon : ext.startsWith("ppt") ? Presentation : FileText);

const STATUS = {
  pending: ["На проверке", "bg-amber-50 text-amber-700 border-amber-200"],
  approved: ["Одобрен · +50", "bg-[#e8f5ed] text-[#1f5f42] border-[#c8e6d3]"],
  rejected: ["Отклонён", "bg-red-50 text-red-700 border-red-200"],
};

export function StatusBadge({ status }) {
  const [label, cls] = STATUS[status];
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${cls}`} data-testid="material-status-badge">{label}</span>;
}

export function MaterialRow({ m, showStatus, showSubject, actions }) {
  const Icon = fileIcon(m.ext);
  const { user, settings, openUpgrade } = useAuth();
  const [view, setView] = useState(null);
  const locked = settings?.downloads_restricted && user.plan === "free" && m.author_id !== user.id;
  const dl = async () => {
    try { await downloadMaterial(m); } catch (e) {
      if (e?.response?.status === 402) openUpgrade("Скачивание файлов доступно в тарифах Basic и Pro");
      else toast.error(errText(e));
    }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5 bg-white border border-[#d1e7dd] rounded-xl hover:shadow-md transition-shadow duration-200" data-testid={`material-row-${m.id}`}>
      <div className="flex items-start gap-4 flex-1 min-w-0">
        <div className="h-11 w-11 shrink-0 rounded-lg bg-[#e8f5ed] text-[#1f5f42] grid place-items-center"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-[#0b2e20] truncate" data-testid="material-title">{m.title}</span>
            <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#0b2e20] text-[#c8f2d6]">{kindName(m.kind)}</span>
            {showStatus && <StatusBadge status={m.status} />}
          </div>
          {m.description && <p className="text-sm text-[#4b6b5c] mt-1 line-clamp-2">{m.description}</p>}
          <div className="text-xs text-[#6b8a7b] mt-1.5">
            {showSubject && <>{subjectById(m.subject)?.name} · {m.grade} класс · </>}
            {m.author} · {m.ext.toUpperCase()} · {formatSize(m.size)} · {formatDate(m.created_at)}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
        {actions}
        <button onClick={() => setView(m)} data-testid={`view-material-${m.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#e8f5ed] text-sm font-semibold text-[#1f5f42] hover:bg-[#d7eee0] transition-colors">
          <Eye className="h-4 w-4" />Смотреть
        </button>
        {!locked && (
          <button onClick={dl} data-testid={`download-material-${m.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#c8e6d3] text-sm font-semibold text-[#1f5f42] hover:bg-[#e8f5ed] transition-colors">
            <Download className="h-4 w-4" />Скачать
          </button>
        )}
      </div>
      <ViewerDialog m={view} onClose={() => setView(null)} />
    </div>
  );
}
